import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';   
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { LoginModel } from 'app/models/LoginResponseModel';
import { ApiResponse } from 'app/models/main/api.response';
import { signUpModel } from 'app/models/signUp.model';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { environment } from 'environments/environment';
import { CookieService } from 'ngx-cookie-service';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    constructor( private _router: Router,private cookieService: CookieService,){}
    apiUrl = environment.apiBaseUrl + '/api/';




    
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    set userDetails(data: string) {
        localStorage.setItem('userDetails', JSON.stringify(data));
    }


    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    get userDetails(): string {
        return localStorage.getItem(JSON.parse('userDetails')) ?? '';
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
        return this._httpClient.post(this.apiUrl + 'Account/ForgotPassword', {email : email}, httpOptions);
    }


    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(body: any): Observable<any> {
        return this._httpClient.post(this.apiUrl + 'Account/SetPassword', body);
    }

    validateResetPasswordLink(email: string, code: string) {
        return this._httpClient.get<ApiResponse>(this.apiUrl+ `Account/ResetPassword?email=${email}&code=${code}`);
    }
    

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<LoginModel> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }
    
        const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    
        return this._httpClient.post(this.apiUrl + 'Account/Login', credentials, httpOptions).pipe(
            switchMap((response: any) => {
                // Check if accessToken or userDetails are blank
                if (!response?.data?.token || !response?.data?.userName) {
                    // Return the response without any error, so it can be handled by the component
                    return of(response);
                }
    
                // Store the access token in the local storage
                this.accessToken = response.data.token;
                this.userDetails = response.data;
    
                // Set the authenticated flag to true
                this._authenticated = true;
    
                // Store the user on the user service
                this._userService.user = response.data.userName;
    
                // Return the response to the component
                return of(response);
            }),
            catchError((error) => {
                // Handle any errors that occur during the HTTP request
                return throwError(error);
            })
        );
    }
    
    

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        const userInfo = localStorage.getItem('userDetails');
        if (userInfo) {
            try {
                var user = JSON.parse(userInfo); // Parse the string into an object
                // Store the access token in the local storage
                this.accessToken = user.token;
                this.userDetails = user;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = user.userName;
            } catch (error) {
                of(false);
            }
        }
        return of(true);
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem("OnchangeSelectedRemediationDashboardId");
        this.cookieService.delete("GlobalSelectedEntityFromHeader", "/");
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userDetails');
        localStorage.removeItem('currentUser');
        localStorage.removeItem('entityChangeCache');
        localStorage.removeItem("OnchangeSelectedParentId") 
        this.cookieService.delete('selectedParentAssessment');
        this.cookieService.delete('selectedParentAssessmentFromRiskReassessment');
        this.cookieService.delete('selectedParentAssessmentFromRiskReassessmentName');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: signUpModel): Observable<any> {
        return this._httpClient.post(this.apiUrl + 'Company/CompanySignUp', user);
        //return this._httpClient.post('api/auth/sign-up', user);
    }

    getDropdownMasterData() {
        return this._httpClient.get<ApiResponse>(this.apiUrl + 'Company/GetMasterData');
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
